import store from '@/store';
import { SOCKET_MESSAGE_TYPE } from '@/constants/socketMessage';
import SocketMessage from '@/classes/v2/SocketMessage';

export default class WebSocketHandler {
  constructor(connectionUrl, options = {}) {
    if (connectionUrl) {
      // Initialize the websocket
      this._websocket = null;
      this._websocketOptions = {};
      this._webSocketUser = null;

      // Class variables
      this.url = connectionUrl;
      this.sessionId = null;
      this.isAuthenticated = false;
      this.isSubscribed = false;

      // Setup auto reconnect
      if (options.reconnect) {
        this._websocketOptions = {
          reconnect: options.reconnect,
          retry: 0,
          maxRetries: options.maxRetries || 10,
          retryTimeout: options.retryTimeout || 5000
        };
      }
    } else {
      throw new Error('WebSocketHandler must be provided a connection URL in the constructor');
    }
  }

  /**
   * User Authentication
   */
  authenticate() {
    if (this._webSocketUser) {
      const authData = {
        messageType: SOCKET_MESSAGE_TYPE.authRequired,
        jwt: this._webSocketUser.token
      };
      const authString = JSON.stringify(authData);
      this._websocket.send(authString);
    } else {
      console.error('Unable to authenticate() - No websocket user is set');
    }
  }

  /**
   * Connect to the WebSocket
   */
  connect() {
    if (this.url) {
      this._websocket = new WebSocket(this.url);
      this.setupListeners();
    }
  }

  /**
   * Reconnects to the WebSocket using options provided
   * in the WebSocketHandler constructor
   */
  reconnect() {
    if (this._websocketOptions.retry < this._websocketOptions.maxRetries) {
      setTimeout(() => {
        console.warn(`Attempting reconnect ${this._websocketOptions.retry + 1} of ${this._websocketOptions.maxRetries}`);
        this._websocketOptions.retry++;
        this.connect();
      }, this._websocketOptions.retryTimeout);
    } else {
      console.warn('Max Retries has been reached. Reconnection will not be attempted');
    }
  }

  /**
   * Register socket event listeners (callbacks)
   */
  setupListeners() {
    this._websocket.onopen = this.handleSocketOpen;
    this._websocket.onclose = this.handleSocketClose;
    this._websocket.onerror = this.handleSocketError;
    this._websocket.onmessage = this.handleSocketMessage;
  }

  /**
   * Sets the user object for the WebSocket
   * @param userObj
   */
  setUserInfo(userId, token, numbers) {
    // Check that the user object has the appropriate fields
      if (userId && token && numbers && numbers.length) {
        this._webSocketUser = { userId, token, numbers };
      } else {
        console.error('Unable to setUser() - Invalid object properties');
      }
  }

  /**
   * Subscribe to a specific set of user events
   */
  subscribe() {
    if (this._webSocketUser) {
      this._webSocketUser.numbers.forEach(n => {
        let subscriptionData = {
          messageType: SOCKET_MESSAGE_TYPE.subscriptionRequest,
          sessionId: this.sessionId,
          number: n.number,
          userId: this._webSocketUser.userId
        };
        const subscriptionString = JSON.stringify(subscriptionData);
        this._websocket.send(subscriptionString);
      });
    } else {
      console.error('Unable to subscribe() - No websocket user is set');
    }
  }

  // Socket Event Handlers
  handleSocketOpen = () => {
    // TODO: Clear retry attempts?
  };

  handleSocketClose = () => {
    console.warn('The socket was closed');
    this.sessionId = null;
    this.isAuthenticated = false;
    this.isSubscribed = false;
    if (this._websocketOptions.reconnect) {
      this.reconnect();
    }
  };

  handleSocketError = () => {
    console.warn('A WebSocket error has occurred');
    if (this._websocketOptions.reconnect) {
      this.reconnect();
    }
  };

  handleSocketMessage = event => {
    const socketMessage = new SocketMessage(JSON.parse(event.data));

    // 1. Authenticate on auth challenge
    if (socketMessage.isAuthChallenge) {
      this.authenticate();
    }

    // 2. Subscribe on auth response
    if (socketMessage.isAuthResponse) {
      if (socketMessage.succeeded) {
        this.isAuthenticated = true;
        this.sessionId = socketMessage.sessionId;
        this.subscribe();
      } else {
        console.error('Unable to authenticate to the real-time server');
      }
    }

    // 3. Subscriptions
    if (socketMessage.isSubscriptionResponse) {
      if (socketMessage.succeeded) {
        this.isSubscribed = true;
      } else {
        console.error('Unable to subscribe to real-time data feed');
      }
    }

    // 4. Messages
    if (socketMessage.isMessageUpdate) {
      store.dispatch('conversationsV2/upsertMessage', socketMessage.message);
    }

    // 5. STOP/UNSTOP Updates
    if (socketMessage.isFlowUpdate) {
      store.dispatch('user/updateBlockedNumbers', socketMessage.message);
    }
  };
}
